/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";

// Material Dashboard 2 React base styles
import colors from "common/assets/theme/base/colors";
import breakpoints from "common/assets/theme/base/breakpoints";
import typography from "common/assets/theme/base/typography";
import boxShadows from "common/assets/theme/base/boxShadows";
import borders from "common/assets/theme/base/borders";
import globals from "common/assets/theme/base/globals";

// Material Dashboard 2 React helper functions
import boxShadow from "common/assets/theme/functions/boxShadow";
import hexToRgb from "common/assets/theme/functions/hexToRgb";
import linearGradient from "common/assets/theme/functions/linearGradient";
import pxToRem from "common/assets/theme/functions/pxToRem";
import rgba from "common/assets/theme/functions/rgba";

// Material Dashboard 2 React components base styles for @mui material components
import sidenav from "common/assets/theme/components/sidenav";
import list from "common/assets/theme/components/list";
import listItem from "common/assets/theme/components/list/listItem";
import listItemText from "common/assets/theme/components/list/listItemText";
import card from "common/assets/theme/components/card";
import cardMedia from "common/assets/theme/components/card/cardMedia";
import cardContent from "common/assets/theme/components/card/cardContent";
import button from "common/assets/theme/components/button";
import iconButton from "common/assets/theme/components/iconButton";
import input from "common/assets/theme/components/form/input";
import inputLabel from "common/assets/theme/components/form/inputLabel";
import inputOutlined from "common/assets/theme/components/form/inputOutlined";
import textField from "common/assets/theme/components/form/textField";
import menu from "common/assets/theme/components/menu";
import menuItem from "common/assets/theme/components/menu/menuItem";
import switchButton from "common/assets/theme/components/form/switchButton";
import divider from "common/assets/theme/components/divider";
import tableContainer from "common/assets/theme/components/table/tableContainer";
import tableHead from "common/assets/theme/components/table/tableHead";
import tableCell from "common/assets/theme/components/table/tableCell";
import linearProgress from "common/assets/theme/components/linearProgress";
import breadcrumbs from "common/assets/theme/components/breadcrumbs";
import slider from "common/assets/theme/components/slider";
import avatar from "common/assets/theme/components/avatar";
import tooltip from "common/assets/theme/components/tooltip";
import appBar from "common/assets/theme/components/appBar";
import tabs from "common/assets/theme/components/tabs";
import tab from "common/assets/theme/components/tabs/tab";
import stepper from "common/assets/theme/components/stepper";
import step from "common/assets/theme/components/stepper/step";
import stepConnector from "common/assets/theme/components/stepper/stepConnector";
import stepLabel from "common/assets/theme/components/stepper/stepLabel";
import stepIcon from "common/assets/theme/components/stepper/stepIcon";
import select from "common/assets/theme/components/form/select";
import formControlLabel from "common/assets/theme/components/form/formControlLabel";
import formLabel from "common/assets/theme/components/form/formLabel";
import checkbox from "common/assets/theme/components/form/checkbox";
import radio from "common/assets/theme/components/form/radio";
import autocomplete from "common/assets/theme/components/form/autocomplete";
import container from "common/assets/theme/components/container";
import popover from "common/assets/theme/components/popover";
import buttonBase from "common/assets/theme/components/buttonBase";
import icon from "common/assets/theme/components/icon";
import svgIcon from "common/assets/theme/components/svgIcon";
import link from "common/assets/theme/components/link";
import dialog from "common/assets/theme/components/dialog";
import dialogTitle from "common/assets/theme/components/dialog/dialogTitle";
import dialogContent from "common/assets/theme/components/dialog/dialogContent";
import dialogContentText from "common/assets/theme/components/dialog/dialogContentText";
import dialogActions from "common/assets/theme/components/dialog/dialogActions";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});
